'use client';

import ListWithSkeleton from '../ui/ListWithSkeleton';
import { useCallback, useRef } from 'react';
import { useMarketList, type MarketListEvent } from '@/hooks/use-market-list';
import { useInViewport } from 'ahooks';
import { useTranslation } from '@/lib/i18n';
import { MarketCard, type MarketCardMarket } from '../cards/market';
import {
  getMarketStatus,
  isMarketResolved,
  getMarketOutcome,
  isWorldCupMarket,
} from '../utils/market';
import { WorldCupMatchCard } from '../world-cup/card';
import { toWorldCupMatchCardDataFromEvent } from '../world-cup/matchCardUtils';

const MARKET_CARD_GROUP_TITLE_MAX_LENGTH = 15;

export interface MarketGridItem {
  id: string;
  href: string;
  market: MarketCardMarket;
}

function truncateGroupTitle(title: string) {
  return title.length > MARKET_CARD_GROUP_TITLE_MAX_LENGTH
    ? title
        .slice(0, MARKET_CARD_GROUP_TITLE_MAX_LENGTH - 3)
        .padEnd(MARKET_CARD_GROUP_TITLE_MAX_LENGTH, '.')
    : title;
}

export function toMarketData(event: MarketListEvent): MarketGridItem {
  const firstMarket = event.markets[0];
  const eventTitle = event.title;
  const poster =
    event.image || event.icon || firstMarket?.market_icon || firstMarket?.market_image || '';
  const yesPrice = firstMarket?.outcome_yes_price ? Number(firstMarket.outcome_yes_price) : 0;
  const change24h = firstMarket?.price_change_24h ? Number(firstMarket.price_change_24h) : 0;
  const totalVolume = event?.volume_24hr ? Number(event.volume_24hr) : 0;
  const groupItemTitle = firstMarket?.group_item_title || 'YES';
  const eventSlug = event.slug || event.event_id || event.id;

  return {
    id: String(event.event_id || event.id),
    href: `/predic/market?id=${eventSlug}`,
    market: {
      marketEventId: String(event.event_id || event.id),
      imageSrc: poster,
      imageAlt: eventTitle,
      title: eventTitle,
      outcome: truncateGroupTitle(groupItemTitle),
      probability: yesPrice * 100,
      change: change24h * 100,
      changePeriod: '(24h)',
      volume: totalVolume,
      category: event.category || event.time_category || '',
      statusLabel: getMarketStatus(firstMarket),
      resolvedOutcome: isMarketResolved(firstMarket) ? getMarketOutcome(firstMarket) : undefined,
    },
  };
}

export default function MarketGrid({
  activeTab,
  wrapperClassName,
}: {
  activeTab: string;
  wrapperClassName?: string;
}) {
  const { t } = useTranslation();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const { list, isLoading, isLoadingMore, hasMore, loadMore } = useMarketList(activeTab, {
    mode: 'infinite',
    limit: 10,
  });

  const handleLoadMoreInViewport = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting && hasMore && !isLoading && !isLoadingMore) {
        loadMore();
      }
    },
    [hasMore, isLoading, isLoadingMore, loadMore],
  );

  useInViewport(loadMoreRef, {
    callback: handleLoadMoreInViewport,
    rootMargin: '240px 0px',
  });

  return (
    <div className="space-y-4">
      <ListWithSkeleton
        loading={isLoading}
        list={list}
        wrapperClassName={wrapperClassName || 'grid grid-cols-2 gap-3'}
        itemRender={(item) => {
          // 世界杯比赛事件用比赛卡片（主队 vs 客队 + 平局概率），其余用通用市场卡片
          if (isWorldCupMarket(item)) {
            const matchData = toWorldCupMatchCardDataFromEvent(item);
            return <WorldCupMatchCard key={matchData.market_event_id} {...matchData} />;
          }
          const marketData = toMarketData(item);
          return (
            <MarketCard
              key={marketData.id}
              marketEventId={marketData.id}
              market={marketData.market}
              href={marketData.href}
            />
          );
        }}
      />
      <div
        ref={loadMoreRef}
        className="flex h-10 items-center justify-center text-sm text-[#757575]"
      >
        {isLoadingMore && t('Loading more...')}
        {!hasMore && list.length > 0 && t('No more markets')}
      </div>
    </div>
  );
}
